/**
 * Touch optimization utilities for golf-specific PWA navigation
 * Optimized for outdoor usage with gloves and one-handed operation
 */

type ElementType = 'link' | 'button' | 'menu'
type HapticType = 'light' | 'medium' | 'heavy' | 'success' | 'error'

interface TouchTargets {
  MINIMUM: number
  STANDARD: number
  OPTIMAL: number
  LARGE: number
}

interface TouchStyles extends React.CSSProperties {
  minHeight: string
  minWidth: string
  display: string
  alignItems: string
  justifyContent: string
  cursor: string
  WebkitTapHighlightColor: string
  touchAction: string
}

const TOUCH_TARGETS: TouchTargets = {
  MINIMUM: 44,
  STANDARD: 48,
  OPTIMAL: 56,
  LARGE: 64
}

export const getTouchOptimizedStyles = (isMobile: boolean, elementType: ElementType = 'link'): Partial<TouchStyles> | Record<string, never> => {
  if (!isMobile) {
    return {}
  }

  const baseStyles: Partial<TouchStyles> = {
    minHeight: `${TOUCH_TARGETS.OPTIMAL}px`,
    minWidth: `${TOUCH_TARGETS.OPTIMAL}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation'
  }

  const elementStyles: Record<ElementType, Partial<TouchStyles>> = {
    link: {
      ...baseStyles,
      padding: '12px 16px',
      textDecoration: 'none'
    },
    button: {
      ...baseStyles,
      padding: '12px 20px',
      border: 'none',
      borderRadius: '8px'
    },
    menu: {
      ...baseStyles,
      padding: '16px 20px',
      width: '100%',
      justifyContent: 'flex-start'
    }
  }

  return elementStyles[elementType] || baseStyles
}

export const triggerHapticFeedback = (type: HapticType = 'light'): void => {
  if (!window.navigator.vibrate) {
    return
  }

  const patterns: Record<HapticType, number | number[]> = {
    light: 10,
    medium: 20,
    heavy: 30,
    success: [10, 50, 10],
    error: [50, 100, 50]
  }

  try {
    window.navigator.vibrate(patterns[type] || patterns.light)
  } catch {
    console.debug('Haptic feedback not available')
  }
}
